// components/frontend/careers/Client/JobApplicationModal.tsx
'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { jobApplicationSchema } from '@/Types/JobApplicationSchema';


type JobApplicationFormData = z.infer<typeof jobApplicationSchema>;

interface JobApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobTitle: string;
  jobId: string;
}

export default function JobApplicationModal({ 
  isOpen, 
  onClose, 
  jobTitle,
  jobId 
}: JobApplicationModalProps) {
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    clearErrors,
  } = useForm<JobApplicationFormData>({
    resolver: zodResolver(jobApplicationSchema),
    mode: 'onBlur', // Validate on blur for better UX
  });

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (file) {
      // Client-side validation
      if (file.type !== 'application/pdf') {
        setValue('cv', undefined as any);
        setCvFile(null);
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setValue('cv', undefined as any);
        setCvFile(null);
        return;
      }
      
      setCvFile(file);
      setValue('cv', file);
      clearErrors('cv');
    }
  };

  const onSubmit = async (data: JobApplicationFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      // Append data - these field names must match what your Express backend expects
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('email', data.email);
      formData.append('cv', data.cv); // Multer expects this field name
      formData.append('jobId', jobId);
      formData.append('jobTitle', jobTitle);
      
      // Make request to Express backend
      const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:5000'}/api/job-applications`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
      });

      // Handle response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to submit application' }));
        throw new Error(errorData.message || 'Failed to submit application');
      }
      
      const result = await response.json();
      
      // Show success message
      setSubmitSuccess(true);
      
      // Reset form and close modal after success
      setTimeout(() => {
        handleClose();
      }, 2500);
      
    } catch (error) {
      console.error('Application submission error:', error);
      setSubmitError(
        error instanceof Error 
          ? error.message 
          : 'Failed to submit application. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setCvFile(null);
    setSubmitSuccess(false);
    setSubmitError(null);
    onClose();
  };

  // Prevent closing during submission
  const handleBackdropClick = () => {
    if (!isSubmitting) {
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-white p-6 relative">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <h2 className="text-[24px] font-semibold pr-12">Apply for Position</h2>
          <p className="text-secondary text-[15px] mt-2 font-medium">{jobTitle}</p>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="absolute inset-0 bg-white z-10 flex items-center justify-center">
            <div className="text-center px-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-[22px] font-semibold text-gray-900 mb-2">
                Application Submitted!
              </h3>
              <p className="text-[15px] text-gray-600">
                We&apos;ve received your application and will review it shortly. Check your email for confirmation.
              </p>
            </div>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-5">
            <div className='flex items-start justify-center md:gap-2'>
                {/* First Name */}
                <div className='flex flex-col w-1/2'>
                <label htmlFor="firstName" className="block text-[14px] font-medium text-gray-700 mb-1.5">
                    First Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="firstName"
                    {...register('firstName')}
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none text-[15px] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                    errors.firstName 
                        ? 'border-red-300 focus:border-red-500' 
                        : 'border-gray-200 focus:border-secondary'
                    }`}
                    placeholder="Enter your first name"
                    autoComplete="given-name"
                />
                {errors.firstName && (
                    <p className="text-red-500 text-[13px] mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.firstName.message}
                    </p>
                )}
                </div>

                {/* Last Name */}
                <div className='flex flex-col w-1/2'>
                <label htmlFor="lastName" className="block text-[14px] font-medium text-gray-700 mb-1.5">
                    Last Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="lastName"
                    {...register('lastName')}
                    disabled={isSubmitting}
                    className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none text-[15px] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                    errors.lastName 
                        ? 'border-red-300 focus:border-red-500' 
                        : 'border-gray-200 focus:border-secondary'
                    }`}
                    placeholder="Enter your last name"
                    autoComplete="family-name"
                />
                {errors.lastName && (
                    <p className="text-red-500 text-[13px] mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.lastName.message}
                    </p>
                )}
                </div>
            </div>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-[14px] font-medium text-gray-700 mb-1.5">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                {...register('email')}
                disabled={isSubmitting}
                className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none text-[15px] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed ${
                  errors.email 
                    ? 'border-red-300 focus:border-red-500' 
                    : 'border-gray-200 focus:border-secondary'
                }`}
                placeholder="your.email@example.com"
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-red-500 text-[13px] mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* CV Upload */}
            <div>
              <label htmlFor="cv" className="block text-[14px] font-medium text-gray-700 mb-1.5">
                Upload CV (PDF only) <span className="text-red-500">*</span>
              </label>
              
              <div className="relative">
                <input
                  type="file"
                  id="cv"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  className="hidden"
                />
                
                <label
                  htmlFor="cv"
                  className={`flex items-center justify-center gap-3 w-full px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isSubmitting 
                      ? 'cursor-not-allowed opacity-50' 
                      : errors.cv 
                      ? 'border-red-300 hover:border-red-400 bg-red-50' 
                      : 'border-gray-300 hover:border-secondary bg-gray-50 hover:bg-secondary/5'
                  }`}
                >
                  {cvFile ? (
                    <>
                      <FileText className="w-6 h-6 text-secondary" />
                      <div className="text-left">
                        <p className="text-[14px] font-medium text-gray-900">{cvFile.name}</p>
                        <p className="text-[12px] text-gray-500">
                          {(cvFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400" />
                      <div className="text-center">
                        <p className="text-[14px] font-medium text-gray-700">
                          Click to upload your CV
                        </p>
                        <p className="text-[12px] text-gray-500 mt-1">
                          PDF only (Max 5MB)
                        </p>
                      </div>
                    </>
                  )}
                </label>
              </div>
              
              {errors.cv && (
                <p className="text-red-500 text-[13px] mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.cv.message}
                </p>
              )}
            </div>

            {/* Submit Error */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-600 text-[14px]">{submitError}</p>
              </div>
            )}

            {/* Privacy Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-[13px] text-blue-800">
                Your personal information will be processed in accordance with our privacy policy and will only be used for recruitment purposes.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-[15px] font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-secondary text-black font-semibold rounded-lg hover:bg-secondary/90 transition-colors text-[15px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}